<script setup>
// Inbox sidebar panel: what's going on in this conversation, at a glance —
// a short summary plus the customer's current mood, from the conda brain.
// Reads the LIVE Chatwoot thread, so it works on purely-human chats too.
import { ref, watch, onMounted } from 'vue';
import { condaApi } from '../condaApi';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const isOpen = ref(true);
const busy = ref(false);
const summary = ref('');
const mood = ref(null);
const stale = ref(false);
const error = ref('');

const MOODS = {
  frustrated: { label: '😤 Frustrated', cls: 'bg-n-ruby-3 text-n-ruby-11' },
  positive: { label: '😊 Positive', cls: 'bg-n-teal-3 text-n-teal-11' },
  neutral: { label: '😐 Neutral', cls: 'bg-n-slate-3 text-n-slate-11' },
};

async function load(refresh = false) {
  busy.value = true;
  error.value = '';
  try {
    const r = await condaApi.assistInsight(props.conversationId, refresh);
    summary.value = r.summary || '';
    mood.value = r.mood;
    stale.value = !!r.stale;
  } catch (e) {
    error.value = e.message;
  } finally {
    busy.value = false;
  }
}

watch(
  () => props.conversationId,
  () => {
    summary.value = '';
    mood.value = null;
    stale.value = false;
    load();
  }
);

onMounted(load);
</script>

<template>
  <div class="text-sm">
    <button
      class="flex items-center select-none w-full rounded-lg bg-n-slate-2 outline outline-1 outline-n-weak m-0 justify-between py-2 px-4"
      :class="{ 'rounded-bl-none rounded-br-none': isOpen }"
      @click.stop="isOpen = !isOpen"
    >
      <h5 class="text-n-slate-12 text-sm mb-0 py-0 pr-2 pl-0">✨ Conda insight</h5>
      <div class="flex items-center gap-2">
        <span
          v-if="mood && !isOpen"
          class="text-[11px] px-1.5 py-0.5 rounded-md"
          :class="MOODS[mood]?.cls"
        >
          {{ MOODS[mood]?.label }}
        </span>
        <div class="flex justify-end w-3 text-n-blue-11 cursor-pointer">
          <fluent-icon v-if="isOpen" size="24" icon="subtract" type="solid" />
          <fluent-icon v-else size="24" icon="add" type="solid" />
        </div>
      </div>
    </button>
    <div
      v-if="isOpen"
      class="outline outline-1 outline-n-weak border-t-0 rounded-br-lg rounded-bl-lg px-3 py-3"
    >
      <div v-if="busy" class="text-xs text-n-slate-10">Reading the conversation…</div>
      <div v-else-if="error" class="text-xs text-n-ruby-11">
        ⚠ {{ error }}
        <button class="ml-1 underline hover:text-n-ruby-12" @click="load(true)">retry</button>
      </div>
      <template v-else>
        <div class="flex items-center gap-2 mb-2">
          <span
            v-if="mood"
            class="text-[11px] px-1.5 py-0.5 rounded-md"
            :class="MOODS[mood]?.cls"
          >
            {{ MOODS[mood]?.label }}
          </span>
          <span v-if="stale" class="text-[11px] text-n-amber-11">may be out of date</span>
          <button
            class="ml-auto text-[11px] text-n-blue-text hover:underline"
            @click="load(true)"
          >
            Refresh
          </button>
        </div>
        <div
          v-if="summary"
          class="text-xs text-n-slate-12 whitespace-pre-wrap leading-relaxed"
        >
          {{ summary }}
        </div>
        <div v-else class="text-xs text-n-slate-10">Nothing to summarize yet.</div>
      </template>
    </div>
  </div>
</template>
